import type { ReactNode, RefObject } from 'react';
import type { Control, FieldValues, Path } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import type { TextInput as RNTextInput } from 'react-native';
import { HelperText } from 'react-native-paper';

import {
  getAllDescendantIds,
  useGetGeoAreaTree
} from '@/features/api/hooks/shared-app/useGetGeoAreaTree';
import { GeoAreaSelectionModal } from '@/shared/components/forms/GeoAreaSelectionModal';
import { useGeoAreaSelection } from '@/shared/components/forms/useGeoAreaSelection';
import { isEmptyOrNull } from '@/shared/utils/string';

/**
 * Arguments passed to {@link Props.renderTrigger}. They expose exactly the
 * state a trigger needs to render itself and open the picker; everything else
 * (pending selection, tree navigation, modal wiring) stays encapsulated here.
 */
export type GeoAreaSelectTriggerArgs = {
  resolvedNames: string;
  /** Total number of sub-areas under the root area (0 while the tree loads). */
  totalDescendantCount: number;
  isOpen: boolean;
  hasError: boolean;
  open: () => void;
  inputRef: RefObject<RNTextInput | null>;
};

type Props<T extends FieldValues> = {
  name: Path<T>;
  control: Control<T>;
  label: string;
  organizationId: string;
  depth?: number;
  sheetTitle?: string;
  helperText?: string;
  editable?: boolean;
  renderTrigger: (args: GeoAreaSelectTriggerArgs) => ReactNode;
};

/**
 * Shared geo-area picker field. Owns the react-hook-form `Controller`, the
 * selection state (via {@link useGeoAreaSelection}) and the single wiring of
 * the {@link GeoAreaSelectionModal}. The visible trigger is supplied by the
 * caller through {@link Props.renderTrigger}, so the modal stays identical
 * while different forms can provide their own trigger (outlined field, list
 * item, …).
 */
export const GeoAreaSelectField = <T extends FieldValues>({
  name,
  control,
  label,
  organizationId,
  depth,
  sheetTitle,
  helperText,
  editable = true,
  renderTrigger
}: Props<T>) => {
  const { data: treeRoots, isLoading } = useGetGeoAreaTree(
    organizationId,
    depth
  );
  const selection = useGeoAreaSelection(treeRoots);

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { value, onChange }, fieldState: { error } }) => {
        const selectedIds = (value as string[] | undefined) ?? [];
        const displayIds = selection.isOpen
          ? selection.pendingIds
          : selectedIds;
        const resolvedNames = selection.resolveNames(displayIds);
        const totalDescendantCount = selection.rootArea
          ? getAllDescendantIds(selection.rootArea).length - 1
          : 0;

        return (
          <>
            {renderTrigger({
              resolvedNames,
              totalDescendantCount,
              isOpen: selection.isOpen,
              hasError: !!error,
              open: () => selection.handleOpen(selectedIds, editable),
              inputRef: selection.inputRef
            })}
            {isEmptyOrNull(error?.message) ? (
              !isEmptyOrNull(helperText) && (
                <HelperText type="info">{helperText}</HelperText>
              )
            ) : (
              <HelperText type="error">{error?.message}</HelperText>
            )}

            <GeoAreaSelectionModal
              selection={selection}
              title={sheetTitle ?? label}
              isLoading={isLoading}
              onDone={() => selection.handleDone(onChange)}
            />
          </>
        );
      }}
    />
  );
};
